import React, { FC, useMemo, useState } from "react";
import ContentHeader from "../../components/Admin/ContentHeader";
import BranchQueueFilter from "../../components/Admin/BranchQueueFilter/BranchQueueFilter";
import EmployeesTable from "../../components/Admin/Tables/EmployeesTable/EmployeesTable";
import classes from "./EmployeesPage.module.scss";
import { ModalEmployees } from "../../components/Admin/Modals/ModalsEmployees/ModalEmployees/ModalEmployees";
import { useGetAllEmployees } from "../../hooks/Admin/EmployeesTable/useGetAllEmployees";
import { usePostNewEmployer } from "../../hooks/Admin/EmployeesTable/usePostNewEmployer";
import { usePutUserData } from "../../hooks/Admin/EmployeesTable/usePutUserData";
import { useDeleteUser } from "../../hooks/Admin/EmployeesTable/useDeleteUser";
import { usePutUserStatus } from "../../hooks/Admin/EmployeesTable/usePutUserStatus";
import { useMyPermissions } from "../../hooks/useMyPermissions";
import { useAdminQueueIdsFilter } from "../../hooks/Admin/useAdminQueueIdsFilter";

const tableName = "Сотрудники";

export type AllEmpoyeesDataType = {
  employeesTableData: EmployerType[];
};

export type EmployerType = {
  id: number;
  employee: string;
  accessLevel: string;
  isActive: boolean;
  isReadOnly: boolean;
  roleId?: number;
  branchNames?: string;
  branchIds?: number[];
  queueIds?: number[];
  photoUrl?: string | null;
};

export type AccessLevelType = {
  id: number;
  roleId?: number;
  accessLevel: string;
  assignedServices: AssignedServicesType[];
};

export type AssignedServicesType = {
  id: number;
  nameRu: string;
  branchIds?: number[];
  queueIds?: number[];
};

function filterEmployeesByBranchQueue(
  employees: EmployerType[],
  branchIds: number[],
  queueIds: number[],
): EmployerType[] {
  if (branchIds.length === 0 && queueIds.length === 0) return employees;
  return employees.filter((row) => {
    const branchMatch =
      branchIds.length === 0 ||
      (row.branchIds?.length &&
        row.branchIds.some((b) => branchIds.includes(b)));
    const queueMatch =
      queueIds.length === 0 ||
      (row.queueIds?.length && row.queueIds.some((q) => queueIds.includes(q)));
    return Boolean(branchMatch && queueMatch);
  });
}

const EmployersPage: FC = () => {
  const { canView, canEdit: canEditPermission } = useMyPermissions();
  const canEdit = canEditPermission("EMPLOYEES_EDIT");
  const canDelete = canEditPermission("EMPLOYEES_DELETE");
  const canChangeActivity = canEditPermission("EMPLOYEES_ACTIVITY");
  const canCreate = canView("EMPLOYEES_CREATE");
  const { queueIdsFilter } = useAdminQueueIdsFilter();

  const [modalEmployeesActive, setModalEmployeesActive] = useState(false);
  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const [activeRowDataEmployer, setActiveRowDataEmployer] =
    useState<EmployerType>(null);

  const [allEmpoyeesData, setAllEmpoyeesData] = useState<AllEmpoyeesDataType>();
  const { isLoadingAllEmployeesData } = useGetAllEmployees(setAllEmpoyeesData);
  const filteredEmployeesData = useMemo(() => {
    const list = allEmpoyeesData?.employeesTableData ?? [];
    return {
      employeesTableData: filterEmployeesByBranchQueue(
        list,
        filterBranchIds,
        filterQueueIds,
      ),
    };
  }, [allEmpoyeesData?.employeesTableData, filterBranchIds, filterQueueIds]);

  const isNoEmployeesForSelectedQueue =
    !isLoadingAllEmployeesData &&
    (filteredEmployeesData.employeesTableData?.length ?? 0) === 0 &&
    filterQueueIds.length > 0;
  const [modalMode, setModalMode] = useState("");
  const { createNewEmployer, isCreateNewEmployerLoading } = usePostNewEmployer(
    setModalEmployeesActive,
  );
  const { updateUserData, isUpdateUserDataLoading } = usePutUserData(
    setModalEmployeesActive,
  );
  const [userDeleteModalActive, setUserDeleteModalActive] = useState(false);
  const { deleteUser, isDeleteUserLoading } = useDeleteUser(
    setUserDeleteModalActive,
  );
  const { updateUserStatus } = usePutUserStatus();

  const handleActiveModalOnCreateBtn = (): void => {
    setActiveRowDataEmployer(null);
    setModalEmployeesActive(true);
  };

  return (
    <div className={classes.container}>
      <>
        <ContentHeader
          name={tableName}
          setModalMode={setModalMode}
          handleButtonClick={
            canCreate ? handleActiveModalOnCreateBtn : undefined
          }
          filterContent={
            <BranchQueueFilter
              selectedBranchIds={filterBranchIds}
              selectedQueueIds={filterQueueIds}
              onBranchChange={setFilterBranchIds}
              onQueueChange={setFilterQueueIds}
              queueIdsFilter={queueIdsFilter}
            />
          }
        />
        {modalEmployeesActive && (
          <ModalEmployees
            isActive={modalEmployeesActive}
            setActive={setModalEmployeesActive}
            activeRowDataEmployer={activeRowDataEmployer}
            createNewEmployer={createNewEmployer}
            updateUserData={updateUserData}
            modalMode={modalMode}
            isLoadingCreate={isCreateNewEmployerLoading}
            isLoadingUpdate={isUpdateUserDataLoading}
          />
        )}

        {allEmpoyeesData && !isNoEmployeesForSelectedQueue && (
          <EmployeesTable
            allEmpoyeesData={filteredEmployeesData}
            setModalActive={setModalEmployeesActive}
            activeRowDataEmployer={activeRowDataEmployer}
            setActiveRowDataEmployer={setActiveRowDataEmployer}
            setModalMode={setModalMode}
            deleteUser={deleteUser}
            updateUserStatus={updateUserStatus}
            userDeleteModalActive={userDeleteModalActive}
            setUserDeleteModalActive={setUserDeleteModalActive}
            isDeleteUserLoading={isDeleteUserLoading}
            isLoadingData={isLoadingAllEmployeesData}
            canEdit={canEdit}
            canDelete={canDelete}
            canChangeActivity={canChangeActivity}
          />
        )}

        {allEmpoyeesData && isNoEmployeesForSelectedQueue && (
          <div className={classes.noEmployeesContainer}>
            <div className={classes.noEmployeesText}>Нет сотрудников</div>
          </div>
        )}
      </>
    </div>
  );
};

export default EmployersPage;
